import { useCallback, useState } from "react";

import type { RfxQuoteTable } from "../../../types/quote.type";
import type { SupplierRow } from "../../../../components/QuoteTable/quote-table.types";
import {
	computeInitialSupplierTotals,
	computeRowTotal,
	type QuoteFieldValues,
} from "../BidderQuoteTab.helpers";

export interface UseSupplierRowTotalsResult {
	/** Per-quote total cost contributed by supplier-created rows. */
	supplierRowCosts: Record<number, number>;
	/** Per-quote, per-response_value-column running totals. */
	supplierColTotals: Record<number, Record<number, number>>;
	/** Callback to pass to SupplierRowsSection.onRowsChange. */
	handleSupplierRowsChange: (quoteId: number, rows: SupplierRow[]) => void;
}

/** Shallow value-equality for a column-id -> number map. */
const sameNumberRecord = (
	a: Record<number, number>,
	b: Record<number, number>,
): boolean => {
	const aKeys = Object.keys(a);
	const bKeys = Object.keys(b);
	if (aKeys.length !== bKeys.length) {
		return false;
	}
	for (const key of aKeys) {
		const colId = Number(key);
		if (a[colId] !== b[colId]) {
			return false;
		}
	}
	return true;
};

/**
 * Owns supplier-row aggregate state for BidderQuoteTab: per-quote total cost
 * and per-response_value-column running totals contributed by supplier-added
 * rows. Seeds from Formik values on mount; updates incrementally as
 * SupplierRowsSection emits row changes.
 */
export const useSupplierRowTotals = (
	listQuotes: RfxQuoteTable[] | undefined,
	rfxInviteId: number | undefined,
	formikValues: QuoteFieldValues | undefined,
): UseSupplierRowTotalsResult => {
	const [supplierRowCosts, setSupplierRowCosts] = useState<Record<number, number>>(
		() => computeInitialSupplierTotals(listQuotes, rfxInviteId, formikValues).costs,
	);
	const [supplierColTotals, setSupplierColTotals] = useState<
		Record<number, Record<number, number>>
	>(
		() => computeInitialSupplierTotals(listQuotes, rfxInviteId, formikValues).perColumn,
	);

	const handleSupplierRowsChange = useCallback(
		(quoteId: number, rows: SupplierRow[]) => {
			const quote = listQuotes?.find((q) => q.id === quoteId);
			const numericColIds = (quote?.columns ?? [])
				.filter((c) => c.columnType === "response_value" || c.columnType === "saving")
				.map((c) => c.id);
			const perCol: Record<number, number> = {};
			for (const colId of numericColIds) {
				perCol[colId] = 0;
			}
			let cost = 0;
			for (const row of rows) {
				const cellValues = row.columnValues ?? {};
				cost += computeRowTotal(quote?.columns, cellValues);
				// Column-totals footer is a flat vertical sum of the response
				// value, independent of computeRowTotal's per-row qty multiplier.
				for (const colId of numericColIds) {
					perCol[colId] += Number(cellValues[colId] ?? 0);
				}
			}
			setSupplierColTotals((prev) => {
				const existing = prev[quoteId];
				if (existing && sameNumberRecord(existing, perCol)) {
					return prev;
				}
				return { ...prev, [quoteId]: perCol };
			});
			setSupplierRowCosts((prev) => {
				if (prev[quoteId] === cost) {
					return prev;
				}
				return { ...prev, [quoteId]: cost };
			});
		},
		[listQuotes],
	);

	return { supplierRowCosts, supplierColTotals, handleSupplierRowsChange };
};
